import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  Mail,
  RefreshCw,
  Sparkles,
  X,
  Pencil,
  Send,
  Clock,
  CircleDashed,
  AlertTriangle,
  Wand2,
  Paperclip,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Database
} from "lucide-react";
import { Card, CardBody, CardHeader } from "../components/Card";
import { Button } from "../components/Button";
import { api, ApiError } from "../lib/api";
import { useMe } from "../lib/me";
import type { CaseDetail, ConnectorEvent, EmailReplySummary, IntentSummary } from "../lib/me";
import { cn } from "../lib/cn";

interface ReclassifyOutcome {
  status: "classified" | "below_threshold" | "no_intents" | "no_inbound_message" | "error";
  intentId?: string | null;
  confidence?: number;
  reason?: string;
  reasoning?: string | null;
}

export default function CaseDetailPage() {
  const { caseId } = useParams();
  const [detail, setDetail] = useState<CaseDetail | null | undefined>(undefined);
  const [intents, setIntents] = useState<IntentSummary[]>([]);
  const [reclassifying, setReclassifying] = useState(false);
  const [lastOutcome, setLastOutcome] = useState<ReclassifyOutcome | null>(null);
  const [reclassifyError, setReclassifyError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (isRefresh = false) => {
    if (!caseId) return;
    if (isRefresh) setRefreshing(true);
    try {
      const d = await api.get<CaseDetail>(`/api/cases/${caseId}`);
      setDetail(d);
    } catch {
      setDetail(null);
    } finally {
      if (isRefresh) setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    api.get<IntentSummary[]>("/api/intents").then(setIntents).catch(() => setIntents([]));
  }, [caseId]);

  const onReclassify = async () => {
    if (!caseId) return;
    setReclassifying(true);
    setReclassifyError(null);
    setLastOutcome(null);
    try {
      const result = await api.post<{ outcome: ReclassifyOutcome }>(
        `/api/cases/${caseId}/reclassify`,
      );
      setLastOutcome(result.outcome);
      await load();
    } catch (err) {
      setReclassifyError((err as ApiError).message ?? "Reclassify failed.");
    } finally {
      setReclassifying(false);
    }
  };

  if (detail === undefined) {
    return (
      <div className="flex h-60 items-center justify-center">
        <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }
  if (detail === null) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <AlertTriangle className="h-10 w-10 text-rose-500 mb-4" />
        <h3 className="text-base font-bold text-slate-900">Case not found</h3>
        <p className="mt-1.5 text-sm text-slate-500">The requested case could not be loaded.</p>
        <Link to="/cases" className="mt-4">
          <Button variant="secondary">Back to cases</Button>
        </Link>
      </div>
    );
  }

  const { case: kase, messages, intent, replies } = detail;
  const activeIntentCount = intents.filter((i) => i.status === "active").length;
  const latestDraft = replies.find((r) => r.status === "drafted");
  const sentReplies = replies.filter((r) => r.status === "sent");
  const failedReplies = replies.filter((r) => r.status === "send_failed");

  return (
    <div className="space-y-6">
      {/* Header breadcrumb & operations */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <Link to="/cases" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-400 hover:text-slate-900 transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to cases
          </Link>
          <h2 className="mt-2 text-xl font-bold tracking-tight text-slate-900 sm:text-2xl truncate">{kase.subject}</h2>
          <p className="mt-1 text-xs font-semibold text-slate-450">
            <span className="text-slate-700">{kase.fromName ?? kase.fromEmail}</span>
            <span className="mx-1.5 text-slate-350">·</span>
            {kase.fromEmail}
          </p>
        </div>
        <Button variant="secondary" onClick={() => load(true)} disabled={refreshing} className="self-start sm:self-center">
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 items-start">
        
        {/* Left main pane (Conversation, workflow pipeline & replies) */}
        <div className="space-y-6 lg:col-span-2">
          
          {/* Conversation Timeline */}
          <Card>
            <CardHeader title="Message history" description="Inbound emails from customer and outgoing updates" />
            <CardBody className="space-y-4">
              {messages.length === 0 ? (
                <p className="text-sm text-slate-500 font-medium text-center py-6">No messages recorded in this case.</p>
              ) : (
                <ul className="space-y-4">
                  {messages.map((m) => {
                    const isInbound = m.direction === "inbound";
                    return (
                      <li
                        key={m.messageId}
                        className={cn(
                          "rounded-2xl border p-5 transition-shadow shadow-[0_2px_8px_rgba(0,0,0,0.01)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.02)]",
                          isInbound
                            ? "border-slate-200/70 bg-white"
                            : "border-brand-100 bg-brand-50/15"
                        )}
                      >
                        <div className="mb-3.5 flex flex-wrap items-center gap-2 text-xs font-medium text-slate-450">
                          <Mail className="h-3.5 w-3.5 shrink-0" />
                          <span className="font-bold text-slate-700">
                            {m.fromName ?? m.fromEmail}
                          </span>
                          <span className="text-slate-350">·</span>
                          <span className="truncate max-w-48 sm:max-w-64">{m.fromEmail}</span>
                          <span className="ml-auto text-[10px] text-slate-400">
                            {new Date(m.receivedAt ?? m.sentAt ?? Date.now()).toLocaleString()}
                          </span>
                        </div>
                        
                        <div className="whitespace-pre-wrap text-sm text-slate-750 leading-relaxed font-medium">
                          {m.plainTextBody}
                        </div>
                        
                        {m.bodyTruncated && (
                          <div className="mt-3.5 flex items-center gap-1.5 rounded-lg bg-amber-50 border border-amber-200/50 p-3 text-xs font-semibold text-amber-800">
                            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
                            <span>Body was truncated for storage. Download the original .eml for full message details.</span>
                          </div>
                        )}

                        <div className="mt-4.5 flex flex-wrap items-center gap-2.5 text-xs">
                          {m.s3Key && (
                            <DownloadEmlButton caseId={kase.caseId} messageId={m.messageId} />
                          )}
                          {m.attachments && m.attachments.length > 0 && (
                            <AttachmentList
                              caseId={kase.caseId}
                              messageId={m.messageId}
                              attachments={m.attachments}
                            />
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardBody>
          </Card>

          {/* Workflow logs */}
          <WorkflowPanel
            caseId={kase.caseId}
            caseStatus={kase.status}
            intentId={kase.intentId}
            connectorId={intent?.connectorId ?? null}
            replies={replies}
            intentSlots={intent?.slots ?? []}
            slotState={kase.slotState ?? {}}
            connectorEvents={detail.connectorEvents ?? []}
            onChanged={load}
          />

          {/* AI draft reply editor */}
          {latestDraft && (
            <DraftPane
              caseId={kase.caseId}
              draft={latestDraft}
              onChanged={load}
            />
          )}

          {/* Sent Replies */}
          {sentReplies.map((r) => (
            <Card key={r.replyId} className="border-emerald-200/70 bg-emerald-50/10">
              <CardHeader
                title={
                  <span className="flex items-center gap-2 text-emerald-800">
                    <Send className="h-4 w-4 text-emerald-700" />
                    Sent reply
                  </span>
                }
                description={`Sent at ${r.sentAt ? new Date(r.sentAt).toLocaleString() : ""}`}
              />
              <CardBody>
                <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 leading-relaxed font-medium">
                  {r.sentBody ?? r.draftedBody}
                </pre>
              </CardBody>
            </Card>
          ))}

          {/* Failed Replies */}
          {failedReplies.map((r) => (
            <Card key={r.replyId} className="border-rose-200/70 bg-rose-50/15">
              <CardHeader
                title={
                  <span className="flex items-center gap-2 text-rose-800">
                    <X className="h-4.5 w-4.5 text-rose-600" />
                    Send failed
                  </span>
                }
                description={`${new Date(r.updatedAt).toLocaleString()} · ${r.model}`}
              />
              <CardBody className="space-y-4">
                {r.errorMessage && (
                  <div className="rounded-xl border border-rose-200 bg-white p-4 text-sm">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-rose-700">
                      Error reported by SES
                    </div>
                    <pre className="mt-2 whitespace-pre-wrap break-words font-mono text-xs text-rose-900 leading-relaxed bg-rose-50/50 p-3 rounded-lg border border-rose-100">
                      {r.errorMessage}
                    </pre>
                  </div>
                )}
                <details className="group">
                  <summary className="cursor-pointer text-xs font-bold text-slate-500 hover:text-slate-900 select-none flex items-center gap-1">
                    <ChevronRight className="h-4.5 w-4.5 transition-transform group-open:rotate-90 text-slate-400" />
                    Show drafted reply body
                  </summary>
                  <pre className="mt-3.5 whitespace-pre-wrap rounded-xl border border-slate-200 bg-white p-4 font-sans text-sm text-slate-700 leading-relaxed font-medium">
                    {r.draftedBody}
                  </pre>
                </details>
              </CardBody>
            </Card>
          ))}
        </div>

        {/* Right side widgets pane */}
        <div className="space-y-6">
          
          {/* Classification details */}
          <Card>
            <CardHeader
              title="AI classification"
              actions={
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={onReclassify}
                  loading={reclassifying}
                  disabled={reclassifying}
                  className="h-8 text-xs px-2.5"
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Reclassify
                </Button>
              }
            />
            <CardBody className="space-y-4">
              {kase.intentId ? (
                <div className="space-y-3.5">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      Matched Category
                    </div>
                    <div className="mt-1.5 flex items-center gap-2">
                      <Sparkles className="h-4.5 w-4.5 text-brand-600 shrink-0" />
                      <span className="font-bold text-slate-900">
                        {intent?.displayName ?? "(unknown)"}
                      </span>
                    </div>
                  </div>
                  <div className="border-t border-slate-100 pt-3 space-y-2.5">
                    <Row label="Confidence" value={`${Math.round((kase.classificationConfidence ?? 0) * 100)}%`} />
                    <Row label="Source" value={kase.classificationSource ?? "—"} />
                    <Row label="Status" value={kase.status} />
                  </div>
                </div>
              ) : (
                <UnclassifiedHint
                  status={kase.status}
                  activeIntentCount={activeIntentCount}
                  outcome={lastOutcome}
                />
              )}

              {reclassifyError && (
                <div className="rounded-xl border border-rose-200 bg-rose-50/60 p-3.5 text-xs font-semibold text-rose-800">
                  {reclassifyError}
                </div>
              )}
            </CardBody>
          </Card>

          {/* Slots details */}
          <Card>
            <CardHeader title="Extracted slots" description="Data parameters pulled from context" />
            <CardBody>
              {Object.keys(kase.slotState ?? {}).length === 0 ? (
                <p className="text-sm text-slate-500 font-medium text-center py-4">No slot parameters extracted yet.</p>
              ) : (
                <ul className="space-y-2.5 text-sm">
                  {Object.entries(kase.slotState).map(([name, slot]) => (
                    <li key={name} className="flex items-center justify-between gap-3 border-b border-slate-100 pb-2.5 last:border-0 last:pb-0">
                      <code className="rounded-md bg-slate-100 px-2 py-0.75 text-xs font-bold text-slate-700">{name}</code>
                      <span className="text-slate-800 font-semibold text-right break-all">
                        {slot.value ?? <em className="text-slate-350 font-normal">empty</em>}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

        </div>
      </div>
    </div>
  );
}

function WorkflowPanel({
  caseId,
  caseStatus,
  intentId,
  connectorId,
  replies,
  intentSlots,
  slotState,
  connectorEvents,
  onChanged,
}: {
  caseId: string;
  caseStatus: string;
  intentId: string | null;
  connectorId: string | null;
  replies: EmailReplySummary[];
  intentSlots: IntentSummary["slots"];
  slotState: Record<string, { value: string | null; confidence?: number; source?: string }>;
  connectorEvents: ConnectorEvent[];
  onChanged: () => void | Promise<void>;
}) {
  const { me } = useMe();
  const tenant = me.tenant;
  const [drafting, setDrafting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const latestReply = replies[0];
  const hasDraft = replies.some((r) => r.status === "drafted");
  const hasSent = replies.some((r) => r.status === "sent");
  const hasFailed = replies.some((r) => r.status === "send_failed");
  const allRejected = replies.length > 0 && replies.every((r) => r.status === "rejected");

  const triggerDraft = async () => {
    setError(null);
    setDrafting(true);
    try {
      await api.post(`/api/cases/${caseId}/draft-reply`);
      setTimeout(async () => { await onChanged(); }, 3000);
      setTimeout(async () => { await onChanged(); }, 8000);
    } catch (err) {
      setError((err as ApiError).message ?? "Couldn't trigger draft.");
    } finally {
      setDrafting(false);
    }
  };

  // Step 1: Classification
  const classificationStep: PipelineStep = !intentId
    ? { state: "blocked", title: "Classification", detail: "Not classified yet — use the Reclassify button." }
    : caseStatus === "below_threshold"
    ? { state: "blocked", title: "Classification", detail: "Confidence below the intent's threshold. Reclassify or adjust the threshold." }
    : { state: "done", title: "Classification", detail: "Done" };

  // Gather step
  type Slot = IntentSummary["slots"][number];
  const requiredSlots: Slot[] = intentSlots.filter((s: Slot) => s.required);

  const satisfiedGroups = new Set<string>();
  for (const s of intentSlots) {
    if (s.required && s.requiredGroup && slotState[s.name]?.value) {
      satisfiedGroups.add(s.requiredGroup);
    }
  }

  const missingSlots: Slot[] = requiredSlots.filter(
    (s: Slot) => !slotState[s.name]?.value && (!s.requiredGroup || !satisfiedGroups.has(s.requiredGroup))
  );
  const filledSlots: Slot[] = requiredSlots.filter((s: Slot) => !!slotState[s.name]?.value);

  let gatherStep: PipelineStep | null = null;
  if (intentId && requiredSlots.length > 0) {
    if (missingSlots.length === 0) {
      gatherStep = {
        state: "done",
        title: "Gather required info",
        detail: `All required slots filled (or satisfied by either-or groups).`,
      };
    } else {
      const ungrouped = missingSlots.filter((s) => !s.requiredGroup);
      const grouped: Record<string, Slot[]> = {};
      for (const s of missingSlots) {
        if (s.requiredGroup) {
          if (!grouped[s.requiredGroup]) {
            grouped[s.requiredGroup] = [];
          }
          grouped[s.requiredGroup]!.push(s);
        }
      }
      const parts: string[] = [];
      for (const s of ungrouped) {
        parts.push(s.label);
      }
      for (const [_, groupSlots] of Object.entries(grouped)) {
        if (!groupSlots || groupSlots.length === 0) continue;
        if (groupSlots.length === 1) {
          parts.push(groupSlots[0]!.label);
        } else {
          parts.push(`Either ${groupSlots.map((s) => s.label).join(" OR ")}`);
        }
      }
      const missingText = parts.join(", ");

      if (caseStatus === "awaiting_slot_fill") {
        gatherStep = {
          state: "pending",
          title: "Asking customer for missing info",
          detail: `Sent a clarification email asking for: ${missingText}.`,
        };
      } else {
        gatherStep = {
          state: "blocked",
          title: "Gather required info",
          detail: `Missing: ${missingText}. Workflow will ask the customer when it runs.`,
        };
      }
    }
  }

  // Connector step
  let connectorStep: { step: PipelineStep; event: ConnectorEvent | null } | null = null;
  if (intentId && connectorId) {
    const latest = connectorEvents[connectorEvents.length - 1] ?? null;
    if (!latest) {
      connectorStep = {
        step: {
          state: "waiting",
          title: "Fetch from connector",
          detail: "Will call the linked connector when slots are ready.",
        },
        event: null,
      };
    } else if (latest.eventType === "connector.invoked") {
      const p = latest.payload;
      connectorStep = {
        step: {
          state: "done",
          title: "Fetched from connector",
          detail: `${p.connectorDisplayName ?? p.urlHost ?? "connector"} → HTTP ${p.httpStatus ?? "?"} in ${p.durationMs ?? "?"}ms · ${p.mappedKeys?.length ?? 0} field(s) extracted`,
        },
        event: latest,
      };
    } else {
      connectorStep = {
        step: {
          state: "error",
          title: "Connector failed",
          detail: `${p_reason(latest)} — reply was drafted without live data.`,
        },
        event: latest,
      };
    }
  }

  // Step 2: Draft
  const draftStep: PipelineStep = hasDraft || hasSent || hasFailed
    ? hasFailed && !hasSent && !hasDraft
      ? { state: "error", title: "Draft reply", detail: "Generation succeeded but send failed — see error below." }
      : { state: "done", title: "Draft reply", detail: latestReply ? `Generated by ${latestReply.model}` : "Done" }
    : caseStatus === "awaiting_slot_fill"
    ? { state: "waiting", title: "Draft reply", detail: "Waiting on customer to provide the missing info." }
    : intentId
    ? caseStatus === "classified" || caseStatus === "awaiting_review"
      ? { state: "pending", title: "Draft reply", detail: "Workflow runs asynchronously. If this stays pending for >1 min, click Generate." }
      : allRejected
      ? { state: "blocked", title: "Draft reply", detail: "Last draft was rejected. Click Generate to try again." }
      : { state: "blocked", title: "Draft reply", detail: "Workflow hasn't run for this case." }
    : { state: "waiting", title: "Draft reply", detail: "Waiting on classification." };

  // Step 3: Send
  let sendStep: PipelineStep;
  if (hasSent) {
    const sent = replies.find((r) => r.status === "sent")!;
    sendStep = {
      state: "done",
      title: "Reply sent",
      detail: `${sent.sentAt ? new Date(sent.sentAt).toLocaleString() : ""}`,
    };
  } else if (hasFailed) {
    sendStep = {
      state: "error",
      title: "Send failed",
      detail: "SES rejected the send. See the failure card below for the exact error.",
    };
  } else if (hasDraft) {
    const blockers: string[] = [];
    if (!tenant?.settings.autoRepliesEnabled) blockers.push("Auto-replies is OFF");
    if (tenant?.settings.dryRunMode) blockers.push("Dry-run mode is ON");
    sendStep = {
      state: "pending",
      title: "Awaiting your approval",
      detail: blockers.length > 0
        ? `Auto-send is blocked: ${blockers.join(", ")}. Approve manually, or change the setting.`
        : "Auto-send didn't fire — likely because the intent's reply mode is 'draft' or confidence < auto-send threshold. Approve manually or change the intent.",
    };
  } else {
    sendStep = { state: "waiting", title: "Send", detail: "Waiting on draft." };
  }

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <Wand2 className="h-4.5 w-4.5 text-slate-400 shrink-0" />
            AI workflow pipeline
          </span>
        }
        description={`Current status: ${caseStatus}`}
        actions={
          intentId && !hasDraft && !hasSent ? (
            <Button size="sm" variant="secondary" onClick={triggerDraft} loading={drafting} className="h-8.5 px-3 text-xs">
              <Sparkles className="h-3.5 w-3.5 mr-1" />
              {allRejected ? "Regenerate" : "Generate draft"}
            </Button>
          ) : null
        }
      />
      <CardBody className="space-y-4">
        <div className="relative pl-1">
          <PipelineStepRow {...classificationStep} />
          {gatherStep && <PipelineStepRow {...gatherStep} />}
          {connectorStep ? (
            <PipelineStepRow {...connectorStep.step}>
              {connectorStep.event && <ConnectorEventDetails event={connectorStep.event} />}
            </PipelineStepRow>
          ) : null}
          <PipelineStepRow {...draftStep} />
          <PipelineStepRow {...sendStep} isLast />
        </div>
        
        {gatherStep && requiredSlots.length > 0 && (
          <div className="ml-7 pt-2 border-t border-slate-100">
            <details className="group">
              <summary className="cursor-pointer text-xs font-bold text-slate-500 hover:text-slate-900 select-none flex items-center gap-0.5">
                <ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90 text-slate-400" />
                Slot status ({filledSlots.length}/{requiredSlots.length})
              </summary>
              <ul className="mt-2.5 space-y-1.5 ml-4">
                {requiredSlots.map((s) => {
                  const v = slotState[s.name]?.value;
                  return (
                    <li key={s.name} className="flex items-center gap-2.5 text-xs font-medium">
                      <code className="rounded-md bg-slate-100 px-2 py-0.5 font-bold text-slate-700">{s.name}</code>
                      <span className={v ? "text-slate-800" : "text-slate-400"}>
                        {v ?? (s.requiredGroup ? `missing (either-or group: ${s.requiredGroup})` : "missing")}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </details>
          </div>
        )}
        
        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50/60 p-3.5 text-xs font-semibold text-rose-800">
            {error}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

type StepState = "done" | "pending" | "blocked" | "error" | "waiting";

interface PipelineStep {
  state: StepState;
  title: string;
  detail: string;
}

function p_reason(e: ConnectorEvent): string {
  if (e.eventType === "connector.failed") {
    const r = e.payload.reason ?? "unknown_error";
    const m = e.payload.message ? `: ${e.payload.message}` : "";
    return `${r}${m}`;
  }
  return "ok";
}

function ConnectorEventDetails({ event }: { event: ConnectorEvent }) {
  const [open, setOpen] = useState(false);
  const p = event.payload;
  const isOk = event.eventType === "connector.invoked";
  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-xs font-bold text-slate-500 hover:text-slate-900 flex items-center gap-1"
      >
        <Database className="h-3.5 w-3.5 text-slate-400" />
        {open ? "Hide input & outputs" : "Show connector logs"}
      </button>
      
      {open && (
        <div className="mt-3 space-y-4.5 rounded-2xl border border-slate-250 bg-slate-950 p-4.5 text-xs text-slate-300 font-mono shadow-inner animate-scale-in">
          <div className="flex flex-wrap items-center justify-between gap-2 text-[10px] text-slate-500 border-b border-slate-800 pb-2">
            <div className="flex items-center gap-2">
              <span className="font-bold text-white">{p.connectorDisplayName ?? "Connector"}</span>
              {p.urlHost && <span>({p.urlHost})</span>}
            </div>
            {p.httpStatus !== undefined && (
              <span className={cn("px-2 py-0.5 rounded-md font-bold", isOk ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" : "bg-rose-500/20 text-rose-450 border border-rose-500/30")}>
                HTTP {p.httpStatus}
              </span>
            )}
          </div>

          {!isOk && (
            <div className="rounded-lg border border-rose-900 bg-rose-950/50 p-3 text-rose-300 leading-normal font-semibold text-xs">
              {p.reason ?? "unknown"}{p.message ? ` — ${p.message}` : ""}
            </div>
          )}

          {p.request && (
            <details className="group">
              <summary className="cursor-pointer font-bold text-slate-400 hover:text-white select-none">
                ▸ View Outbound Request
              </summary>
              <div className="mt-2.5 space-y-2.5 pl-3 border-l border-slate-800">
                <div className="rounded border border-slate-800 bg-slate-900 px-2.5 py-1.5 break-all">
                  <span className="text-emerald-400 font-bold">{p.request.method}</span>{" "}
                  <span className="text-slate-350">{p.request.url}</span>
                </div>
                {p.request.body && (
                  <div>
                    <div className="text-[10px] uppercase font-bold text-slate-500">Payload body</div>
                    <pre className="mt-1 max-h-48 overflow-auto rounded border border-slate-800 bg-slate-900 p-2 text-slate-300 whitespace-pre-wrap">
                      {p.request.body}
                    </pre>
                  </div>
                )}
              </div>
            </details>
          )}

          {p.response && (
            <details className="group">
              <summary className="cursor-pointer font-bold text-slate-400 hover:text-white select-none">
                ▸ View Raw Response Output
              </summary>
              <div className="mt-2.5 pl-3 border-l border-slate-800">
                {p.response.bodyText !== undefined && p.response.bodyText !== null && (
                  <pre className="max-h-48 overflow-auto rounded border border-slate-800 bg-slate-900 p-2.5 text-slate-300 whitespace-pre-wrap">
                    {p.response.bodyText || "(empty response)"}
                  </pre>
                )}
                {p.response.truncated && (
                  <p className="mt-1 text-[10px] text-amber-500 font-semibold">
                    Response capped at 100KB capacity limit.
                  </p>
                )}
              </div>
            </details>
          )}

          {p.mapped && p.mapped.length > 0 && (
            <details open className="group">
              <summary className="cursor-pointer font-bold text-slate-400 hover:text-white select-none">
                ▾ Extracted values mapped to AI ({p.mapped.length})
              </summary>
              <div className="mt-2.5 space-y-2.5 pl-3 border-l border-slate-800">
                {p.mapped.map((f) => {
                  const customer = f.visibility === "customer";
                  return (
                    <div
                      key={f.name}
                      className={cn(
                        "rounded-lg border p-2.5",
                        customer ? "border-emerald-800/80 bg-emerald-950/20 text-emerald-300" : "border-slate-800 bg-slate-900/50 text-slate-300"
                      )}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <code className="text-white font-bold">{f.name}</code>
                        <span className={cn("rounded-md px-1.5 py-0.25 text-[9px] font-bold uppercase", customer ? "bg-emerald-500/20 text-emerald-450" : "bg-slate-800 text-slate-400")}>
                          {customer ? "Customer facing" : "Internal context"}
                        </span>
                      </div>
                      <pre className="overflow-x-auto whitespace-pre-wrap break-words text-[11px]">
                        {f.valuePreview}
                      </pre>
                    </div>
                  );
                })}
                <p className="text-[10px] text-slate-500 leading-normal">
                  Green items are allowed to be quoted in response drafts. Gray items are internal-only constraints.
                </p>
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function PipelineStepRow({
  state,
  title,
  detail,
  isLast,
  children,
}: PipelineStep & {
  isLast?: boolean;
  children?: React.ReactNode;
}) {
  const { Icon, color } = stepIcon(state);
  return (
    <div className="relative pb-4 last:pb-0">
      {!isLast && (
        <span
          className="absolute left-[11px] top-6 bottom-0 w-[2px] bg-slate-100"
          aria-hidden="true"
        />
      )}
      <div className="flex items-start gap-3">
        <div className="relative z-10 flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-full bg-white shadow-2xs border border-slate-100">
          <Icon className={cn("h-4 w-4 shrink-0", color)} />
        </div>
        <div className="min-w-0 flex-1 pt-0.5">
          <p className="text-sm font-bold text-slate-900 leading-none">{title}</p>
          <p className="mt-1 text-xs text-slate-550 leading-relaxed font-medium">{detail}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

function stepIcon(state: StepState): { Icon: typeof Check; color: string } {
  switch (state) {
    case "done": return { Icon: Check, color: "text-emerald-600" };
    case "pending": return { Icon: Clock, color: "text-amber-500 animate-pulse" };
    case "blocked": return { Icon: AlertTriangle, color: "text-amber-500" };
    case "error": return { Icon: X, color: "text-rose-600" };
    case "waiting":
    default: return { Icon: CircleDashed, color: "text-slate-300" };
  }
}

function DraftPane({
  caseId,
  draft,
  onChanged,
}: {
  caseId: string;
  draft: EmailReplySummary;
  onChanged: () => void | Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(draft.draftedBody);
  const [busy, setBusy] = useState<"approve" | "reject" | "save" | "regenerate" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const approve = async () => {
    setError(null);
    setBusy("approve");
    try {
      const result = await api.post<{ outcome: { status: string; reason?: string } }>(
        `/api/cases/${caseId}/replies/${draft.replyId}/approve`,
        { editedBody: editing ? body : undefined },
      );
      if (result.outcome.status === "awaiting_review") {
        setError("Dry-run mode is ON — approved but not sent. Disable Dry-run in Settings (or left panel) to send emails.");
      } else if (result.outcome.status === "skipped") {
        setError(`Send was skipped: ${result.outcome.reason ?? "unknown reason"}.`);
      }
      await onChanged();
    } catch (err) {
      setError((err as ApiError).message ?? "Send failed.");
    } finally {
      setBusy(null);
    }
  };

  const reject = async (regenerate: boolean) => {
    setError(null);
    setBusy(regenerate ? "regenerate" : "reject");
    try {
      await api.post(`/api/cases/${caseId}/replies/${draft.replyId}/reject`, {
        reason: regenerate ? "regenerate_request" : "rejected",
        regenerate,
      });
      await onChanged();
    } catch (err) {
      setError((err as ApiError).message ?? "Reject failed.");
    } finally {
      setBusy(null);
    }
  };

  const saveEdit = async () => {
    setError(null);
    setBusy("save");
    try {
      await api.post(`/api/cases/${caseId}/replies/${draft.replyId}/edit`, {
        editedBody: body,
      });
      setEditing(false);
      await onChanged();
    } catch (err) {
      setError((err as ApiError).message ?? "Edit failed.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card className="border-brand-300 bg-brand-50/10">
      <CardHeader
        title={
          <span className="flex items-center gap-2 text-brand-900">
            <Sparkles className="h-4.5 w-4.5 text-brand-600 animate-pulse shrink-0" />
            AI draft reply
          </span>
        }
        description={`${draft.model} · ${draft.inputTokens}→${draft.outputTokens} tokens · ${draft.generatedBy}`}
        actions={
          editing ? null : (
            <Button size="sm" variant="ghost" onClick={() => setEditing(true)} className="h-8 text-xs font-bold">
              <Pencil className="h-3.5 w-3.5 mr-1" /> Edit draft
            </Button>
          )
        }
      />
      <CardBody className="space-y-4">
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-medium text-slate-500">
          <span className="font-bold text-slate-700">Subject line:</span> {draft.draftedSubject}
        </div>

        {editing ? (
          <textarea
            rows={12}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="block w-full rounded-xl border border-slate-200 bg-white p-4 font-mono text-sm leading-relaxed focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/10 transition-all"
          />
        ) : (
          <pre className="whitespace-pre-wrap rounded-xl border border-slate-200 bg-white p-5 font-sans text-sm text-slate-700 leading-relaxed font-medium">
            {draft.draftedBody}
          </pre>
        )}

        {error && (
          <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3.5 text-xs font-semibold text-amber-800">
            {error}
          </div>
        )}

        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 border-t border-slate-100/80">
          <div className="flex gap-2">
            {editing && (
              <>
                <Button size="sm" variant="secondary" onClick={() => { setEditing(false); setBody(draft.draftedBody); }} disabled={!!busy}>
                  Cancel
                </Button>
                <Button size="sm" onClick={saveEdit} loading={busy === "save"}>
                  Save changes
                </Button>
              </>
            )}
          </div>

          <div className="flex gap-2 w-full sm:w-auto justify-end">
            <Button size="sm" variant="secondary" onClick={() => reject(true)} loading={busy === "regenerate"} disabled={!!busy}>
              <RefreshCw className="h-3.5 w-3.5 mr-1" /> Regenerate
            </Button>
            <Button size="sm" variant="secondary" onClick={() => reject(false)} loading={busy === "reject"} disabled={!!busy}>
              <X className="h-3.5 w-3.5 mr-1" /> Reject
            </Button>
            <Button size="sm" onClick={approve} loading={busy === "approve"} disabled={!!busy}>
              <Check className="h-3.5 w-3.5 mr-1" /> {editing ? "Save & Send" : "Approve & Send"}
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 text-xs font-medium">
      <span className="text-slate-450">{label}</span>
      <span className="font-bold text-slate-905">{value}</span>
    </div>
  );
}

function UnclassifiedHint({
  status,
  activeIntentCount,
  outcome,
}: {
  status: string;
  activeIntentCount: number;
  outcome: ReclassifyOutcome | null;
}) {
  if (outcome?.status === "no_intents" || activeIntentCount === 0) {
    return (
      <div className="space-y-2">
        <p className="text-sm font-medium text-slate-700 leading-normal">
          No active categories configured. The classifier requires at least one active intent.
        </p>
        <p className="text-xs text-slate-500 leading-normal font-medium">
          Set up category intents from starter packs, or{" "}
          <Link to="/categories/new" className="text-brand-600 underline font-bold">
            create one manually
          </Link>
          .
        </p>
      </div>
    );
  }
  if (outcome?.status === "error") {
    return (
      <div className="space-y-1.5">
        <p className="text-sm font-bold text-rose-700">Classification failed.</p>
        <p className="text-xs text-slate-500 break-words leading-normal font-medium">
          Error parameter: <code className="rounded bg-rose-50 border border-rose-100 px-1.5 py-0.5 text-xs text-rose-800 font-bold">{outcome.reason}</code>
        </p>
      </div>
    );
  }
  if (status === "below_threshold") {
    return (
      <p className="text-xs font-semibold text-slate-500 leading-normal">
        Classifier wasn't confident enough to match a category. Adjust threshold limits or click Reclassify to run again.
      </p>
    );
  }
  return (
    <p className="text-xs font-semibold text-slate-500 leading-normal">
      Not yet classified. Click the Reclassify button to run the AI classifier on this ticket.
    </p>
  );
}

interface AttachmentMeta {
  contentType: string;
  filename: string;
  sizeBytes: number;
}

function AttachmentList({ caseId, messageId, attachments }: {
  caseId: string;
  messageId: string;
  attachments: AttachmentMeta[];
}) {
  const [opening, setOpening] = useState<number | null>(null);

  const open = async (idx: number) => {
    setOpening(idx);
    try {
      const r = await api.get<{ url: string }>(
        `/api/cases/${caseId}/messages/${messageId}/attachments/${idx}`,
      );
      window.open(r.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      const e = err as ApiError;
      alert(e.message ?? "Couldn't open attachment.");
    } finally {
      setOpening(null);
    }
  };

  return (
    <div className="flex flex-wrap gap-1.5">
      {attachments.map((a, i) => (
        <button
          key={i}
          type="button"
          onClick={() => open(i)}
          disabled={opening === i}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-700 hover:border-slate-350 hover:bg-slate-100/80 transition-all font-semibold select-none cursor-pointer disabled:opacity-50"
        >
          {opening === i ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Paperclip className="h-3 w-3 text-slate-400" />}
          <span className="max-w-[140px] truncate">{a.filename}</span>
          <span className="text-[10px] text-slate-400 font-medium">{formatSize(a.sizeBytes)}</span>
          <ExternalLink className="h-3 w-3 text-slate-400" />
        </button>
      ))}
    </div>
  );
}

function DownloadEmlButton({ caseId, messageId }: { caseId: string; messageId: string }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get<{ url: string; filename: string }>(
        `/api/cases/${caseId}/messages/${messageId}/eml`,
      );
      window.location.href = r.url;
    } catch (err) {
      const e = err as ApiError;
      alert(e.message ?? "Couldn't download .eml.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={download}
      disabled={busy}
      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-700 hover:border-slate-350 hover:bg-slate-50 transition-all font-semibold select-none cursor-pointer disabled:opacity-50"
    >
      {busy ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3 text-slate-400" />}
      Download original .eml
    </button>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}
